import React from 'react';
import { Sun, Moon, Sunrise, Sunset, Clock } from 'lucide-react';

// Time slot definitions
const TIME_SLOTS = [
  {
    id: 'night',
    name: 'Nuit',
    description: 'Période nocturne',
    icon: Moon,
    startTime: 0, // 00:00
    endTime: 5,   // 05:00
    color: '#1e293b'
  },
  {
    id: 'pre-sunrise',
    name: 'Avant lever',
    description: 'Heure bleue du matin',
    icon: Clock,
    startTime: 5,  // 05:00
    endTime: 7,    // 07:00  
    color: '#3730a3'
  },
  {
    id: 'sunrise',
    name: 'Lever de soleil',
    description: 'Heure dorée matinale',
    icon: Sunrise,
    startTime: 7,  // 07:00
    endTime: 9,    // 09:00
    color: '#ea580c'
  },
  {
    id: 'morning',
    name: 'Matinée',
    description: 'Milieu de matinée',
    icon: Sun,
    startTime: 9,  // 09:00
    endTime: 12,   // 12:00
    color: '#fbbf24'
  },
  {
    id: 'midday',
    name: 'Midi',
    description: 'Milieu de journée',
    icon: Sun,
    startTime: 12, // 12:00
    endTime: 14,   // 14:00
    color: '#f59e0b'
  },
  {
    id: 'afternoon',
    name: 'Après-midi',
    description: 'Fin d\'après-midi',
    icon: Sun,
    startTime: 14, // 14:00
    endTime: 17,   // 17:00
    color: '#d97706'
  },
  {
    id: 'pre-sunset',
    name: 'Début coucher',
    description: 'Heure dorée du soir',
    icon: Clock,
    startTime: 17, // 17:00
    endTime: 19,   // 19:00
    color: '#c2410c'
  },
  {
    id: 'sunset',
    name: 'Coucher de soleil',
    description: 'Heure dorée et crépuscule',
    icon: Sunset,
    startTime: 19, // 19:00
    endTime: 21,   // 21:00
    color: '#dc2626'
  },
  {
    id: 'evening',
    name: 'Soirée',
    description: 'Fin de soirée',
    icon: Moon,
    startTime: 21, // 21:00
    endTime: 24,   // 24:00
    color: '#7c3aed'
  }
];

export function TimeSlotSelector({
  selectedTimeSlots = [], // Array of selected time slot IDs
  onTimeSlotsChange = () => {},
  disabled = false
}) {
  // Handle time slot selection/deselection
  const handleTimeSlotToggle = (slotId) => {
    if (selectedTimeSlots.includes(slotId)) {
      // Remove time slot (deselect)
      onTimeSlotsChange(selectedTimeSlots.filter(id => id !== slotId));
    } else {
      // Add time slot (select)
      onTimeSlotsChange([...selectedTimeSlots, slotId]);
    }
  };

  // Format time for display
  const formatTime = (hour) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  return (
    <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">
          Période de la journée
        </h3>
        <p className="text-xs text-gray-600">
          Sélectionnez les moments optimaux pour visiter
        </p>
      </div>

      {/* Time slots grid */}
      <div className="grid grid-cols-3 gap-2">
        {TIME_SLOTS.map((slot) => {
          const Icon = slot.icon;
          const isSelected = selectedTimeSlots.includes(slot.id);
          
          return (
            <button
              key={slot.id}
              onClick={() => !disabled && handleTimeSlotToggle(slot.id)}
              disabled={disabled}
              className={`flex flex-col items-center p-2 rounded-lg text-left transition-all duration-200 border text-xs ${
                isSelected 
                  ? 'border-2 shadow-md transform scale-105'
                  : 'border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              style={isSelected ? { 
                backgroundColor: `${slot.color}15`, 
                borderColor: slot.color,
                color: slot.color
              } : {}}
              title={`${slot.description} (${formatTime(slot.startTime)} - ${formatTime(slot.endTime)})`}
            >
              <div className="flex items-center space-x-1 mb-1">
                <Icon size={14} className={isSelected ? 'text-current' : 'text-gray-500'} />
                {isSelected && <span className="text-xs">✓</span>}
              </div>
              
              <div className="text-center">
                <div className="font-medium leading-tight mb-0.5">
                  {slot.name}
                </div>
                <div className="text-xs opacity-70 leading-tight">
                  {formatTime(slot.startTime)}-{formatTime(slot.endTime)}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected slots summary */}
      {selectedTimeSlots.length > 0 && (
        <div className="mt-3 p-2 bg-white/60 rounded border">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">
              {selectedTimeSlots.length} période{selectedTimeSlots.length > 1 ? 's' : ''} sélectionnée{selectedTimeSlots.length > 1 ? 's' : ''}
            </span>
            <button
              onClick={() => onTimeSlotsChange([])}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
            >
              Effacer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { TIME_SLOTS };